/**
 * Chip layer for HighLow.
 *
 * Flutter HighLow has NO Flame chip-flight implementation — chip animations
 * are driven entirely inside the Rive artboards. For the Pixi port we still
 * render chips above the betting Rive canvas so the player gets clear visual
 * feedback on placement and win/lose.
 *
 * Two bet areas only — High and Low (no Tie).
 * Coordinate system: 1792×828 reference (PixiLayer's logical size).
 *
 * The High/Low scatter rectangles are tuned to sit roughly over the High and
 * Low buttons rendered by `highlowbetting.riv`. They are coarse — re-measure
 * once the artboard is visible.
 */
import { Container } from 'pixi.js';
import type { BetType } from '../types/game';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

interface ScatterRect {
  minX: number;
  rangeX: number;
  minY: number;
  rangeY: number;
}

// Chip-landing rectangles, tuned to the betting band the High/Low buttons now
// occupy (index.html #rive-canvas-betting: 30%×30% centred at 49.5%/74.7% ⇒
// x∈[34.5%,64.5%], y∈[59.7%,89.7%]). High = left half of the band, Low = right
// half — matching highlowbetting.riv's left/right button layout. Coarse:
// re-measure against the rendered artboard if chips look off-centre.
const SCATTER: Record<BetType, ScatterRect> = {
  high: {
    minX: 0.36 * REF_WIDTH,
    rangeX: 130,
    minY: 0.62 * REF_HEIGHT,
    rangeY: 110,
  },
  low: {
    minX: 0.52 * REF_WIDTH,
    rangeX: 130,
    minY: 0.62 * REF_HEIGHT,
    rangeY: 110,
  },
};

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };

interface ActiveChip {
  bet: BetType;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  placeChip(bet: BetType, chipValue: number, chipIndex: number): void {
    const rect = SCATTER[bet];
    const target: XY = {
      x: rect.minX + Math.random() * rect.rangeX,
      y: rect.minY + Math.random() * rect.rangeY,
    };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300,
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ bet, sprite });
  }

  animateWinningChips(winningBets: ReadonlySet<BetType>): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const target = winningBets.has(c.bet) ? PLAYER_WIN_TARGET : DEALER_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  animateLosingChips(): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => this.flyAndFade(c, DEALER_TARGET, i));
    this.scheduleDestroyAll(oldActive, 600);
  }

  /** Tie (push) — flutter HighLow doesn't actually expose a Tie bet, but the
   *  server may still settle a push (firstCard rank == secondCard rank). Split
   *  chips half/half to mirror Dragon Tiger's tie animation. */
  animateTie(): void {
    const oldActive = this.active;
    this.active = [];
    const half = Math.ceil(oldActive.length / 2);
    oldActive.forEach((c, i) => {
      const target = i < half ? DEALER_TARGET : PLAYER_WIN_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  private flyAndFade(c: ActiveChip, target: XY, index: number): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
